import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Avatar, Button, Chip, Collapse, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EXTENSION_URL } from 'env';
import { postBoardAiSuggestPins } from '../api/boardAiApi';
import { applyBoardAiActions, hasBoardAiMutations } from '../utils/applyBoardAiActions';
import { extractBoardDocumentSnapshot } from '../utils/boardSnapshot';
import { syncPinShapes } from '../utils/syncPinShapes';
import { buildClientContext } from '../utils/buildClientContext';
import { boardConfirm } from '../utils/boardAlert';
import {
  isActionLabelTruncated,
  openExternalAsk,
  openGoogleSearch,
  truncateActionLabel,
} from '../utils/openExternalAi';
import { buildExternalAskPrompt } from '../utils/buildExternalAskSelectionPrompt';
import { collectRelatedAskContextItems } from '../utils/collectRelatedAskContext';
import { buildSuggestPinQueries } from '../utils/selectionContextCost';
import {
  actionKeyForNextAction,
  dedupeBlockers,
  getActionKeyMarks,
  groupLinksByBlocker,
  linkBlockersToActions,
} from '../utils/linkBlockersToActions';
import {
  computeGoalProgressRate,
  doNextRefreshWouldMatter,
  partialPercentForBlocker,
} from '../utils/boardContextRecommendations';
import { boardNeedsStepsConstraintsPatch } from '../utils/boardConstraintsDigest';
import { resolveGoalStepStatus } from '../utils/goalHistory';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { useAskProvider } from '../hooks/useAskProvider';
import { useAiThinkingStatus } from '../hooks/useAiThinkingStatus';
import { getCanvasMessages, getKindPrefix } from '../utils/canvasI18n';
import { useBoardStore } from '../store/useBoardStore';
import { readCurrentStep } from '../utils/boardGoalFields';
import { currentStep as selectCurrentStep, findStep } from '../utils/steps';
import { useBoardAiContext } from '../context/BoardAiContext';
import { getActivityId } from '../utils/autoLayout';
import BoardAiThinking from './BoardAiThinking';
import BoardAiJobDebug from './BoardAiJobDebug';
import BoardAiInputDebug from './BoardAiInputDebug';
import BoardCurrentStep from './BoardCurrentStep';
import BoardPlanModal from './BoardPlanModal';
import HowToPinButton from './HowToPinButton';
import { AskProviderIcon, AskProviderStartIcon } from './AskProviderIcons';
import { placeDecisionTextOnBoard } from '../utils/placeDecisionTextOnBoard';

const GoogleIcon = ({ size = 16 }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    sx={{ width: size, height: size, display: 'block' }}
  >
    <path
      fill="#EA4335"
      d="M12 10.2v3.9h5.5c-.2 1.2-1.4 3.5-5.5 3.5-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.2 14.7 2.2 12 2.2c-5.4 0-9.8 4.4-9.8 9.8s4.4 9.8 9.8 9.8c5.7 0 9.5-4 9.5-9.6 0-.6-.1-1.1-.2-1.6H12z"
    />
    <path
      fill="#34A853"
      d="M2.2 7.8l3.2 2.3c.9-2.7 3.4-4.5 6.6-4.5 1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.2 14.7 2.2 12 2.2c-3.8 0-7.2 2.2-8.8 5.6z"
    />
    <path
      fill="#FBBC05"
      d="M12 21.8c2.6 0 4.8-.9 6.4-2.5l-3-2.5c-.8.6-2 1.1-3.4 1.1-3.1 0-5.7-2.1-6.6-4.9l-3.1 2.4c1.6 3.5 5.1 6.4 9.7 6.4z"
    />
    <path
      fill="#4285F4"
      d="M21.5 12.2c0-.6-.1-1.1-.2-1.6H12v3.9h5.5c-.3 1.2-1 2.1-2 2.8l3 2.5c1.7-1.6 2.9-3.9 2.9-7.6z"
    />
  </Box>
);

const ActionLinkMarks = ({ marks = [] }) => {
  if (!marks.length) return null;
  return (
    <span className="board-ai-link-marks" aria-hidden>
      {marks.map(mark => (
        <span key={mark} className="board-ai-link-mark">{mark}</span>
      ))}
    </span>
  );
};

const RECOMMENDATION_THUMB_SX = {
  width: 20,
  height: 20,
  fontSize: 12,
};

const EMPTY_SEARCH_SUGGESTIONS = [];
const EMPTY_NEXT_ACTIONS = [];

const KIND_META = {
  search: { prefix: 'Search', Icon: SearchIcon },
  ask: { prefix: 'Ask', Icon: SmartToyOutlinedIcon },
  place: { prefix: 'Pin', Icon: DashboardCustomizeOutlinedIcon },
  decide: { prefix: 'Decide', Icon: HelpOutlineIcon },
  'set-goal': { prefix: 'Goal', Icon: FlagOutlinedIcon },
  'add-text': { prefix: 'Note', Icon: NotesOutlinedIcon },
  apply: { prefix: 'Apply', Icon: NotesOutlinedIcon },
};

/** Effective action a plan option / custom answer will run (matches handleDecideAnswer). */
function resolvePlanActionKind(option = null, { hasCustomAnswer = false, actionKind = '' } = {}) {
  if (option) {
    const kind = String(option.kind || '').trim().toLowerCase();
    // Explicit kind wins. Never infer set-goal from a stray `goal` field — the model
    // often fills goal on every option from the schema, which incorrectly overwrote
    // the current goal when users clicked Search/Ask/Decide choices.
    if (KIND_META[kind] && kind !== 'decide' && kind !== 'apply') return kind;
    if (option.query) return 'search';
    if (option.question) return 'ask';
    if (option.text) return 'add-text';
  }
  // Custom answer inside a set-goal job adopts that text as the current-goal step.
  if (actionKind === 'set-goal') return 'set-goal';
  if (hasCustomAnswer || !option) return 'apply';
  return 'apply';
}

/** Jobs that need multi-step user input open in a centered modal. */
function needsPlanModal(action) {
  const kind = action?.kind || 'decide';
  if (kind === 'decide') return true;
  if (kind === 'set-goal' && action?.options?.length) return true;
  return false;
}

/** Short Do-next label used in post-decide “what’s next” status. */
function briefNextActionLabel(action, language = 'en') {
  if (!action) return '';
  const kind = action.kind || 'decide';
  const prefix = getKindPrefix(kind, language);
  const core = truncateActionLabel(
    action.label || action.query || action.question || action.goal || '',
  );
  if (!core) return prefix;
  return `${prefix}: ${core}`;
}

function formatPlanOptionLabel(option, language = 'en') {
  const label = String(option?.label || '').trim();
  if (!label) return '';
  const kind = resolvePlanActionKind(option);
  const prefix = getKindPrefix(kind, language);
  // Avoid "Search: Search: …" when the model already prefixed the label.
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (new RegExp(`^${escaped}\\s*:`, 'i').test(label)) return label;
  return `${prefix}: ${label}`;
}

function RecommendationThumb({ imageUrl, fallbackIcon: FallbackIcon }) {
  const [failed, setFailed] = useState(false);

  if (imageUrl && !failed) {
    return (
      <Avatar
        src={imageUrl}
        alt=""
        variant="rounded"
        imgProps={{
          loading: 'lazy',
          referrerPolicy: 'no-referrer',
          onError: () => setFailed(true),
        }}
        sx={RECOMMENDATION_THUMB_SX}
      />
    );
  }
  return <FallbackIcon fontSize="small" />;
}

const RECOMMENDATION_TOOLTIP_PROPS = {
  enterDelay: 0,
  enterNextDelay: 0,
  arrow: true,
  placement: 'top',
  slotProps: {
    tooltip: {
      sx: { maxWidth: 360 },
    },
  },
};

function buildAskPrompt({ question, languageLabel }) {
  // Goal / progress / gaps ship via chat scene context — keep the visible message short.
  return [
    question ? `Question: ${question}` : '',
    `Please respond in ${languageLabel}.`,
  ]
    .filter(Boolean)
    .join('\n');
}

function buildSearchPrompt({ query, languageLabel }) {
  // Goal / progress / gaps ship via chat scene context — keep the visible message short.
  return [
    `Search the web for: ${query}`,
    'Summarize the most useful findings for this board and cite sources.',
    `Please respond in ${languageLabel}.`,
  ]
    .filter(Boolean)
    .join('\n');
}

function blockerDoneKey(blocker, index) {
  const title = String(blocker?.title || '').trim();
  return title || `blocker:${index}`;
}

/** Highest-priority open gap (blockers are already priority-sorted). */
function findPriorityBlockerIndex(blockers, doneKeys, notInterestedKeys, linksByBlocker = null) {
  if (!Array.isArray(blockers) || !blockers.length) return -1;
  let firstOpen = -1;
  for (let index = 0; index < blockers.length; index += 1) {
    const key = blockerDoneKey(blockers[index], index);
    if (doneKeys.has(key) || notInterestedKeys.has(key)) continue;
    if (firstOpen < 0) firstOpen = index;
    if (!linksByBlocker) return index;
    const links = linksByBlocker.get(index) || [];
    if (links.length > 0) return index;
  }
  return firstOpen;
}

const PROGRESS_BUMP_PER_BLOCKER = 5;
const PROGRESS_BUMP_CAP = 95;
const DO_NEXT_EXPANDED_STORAGE_KEY = 'hashely.canvas.doNextExpanded';

function readDoNextExpanded() {
  try {
    const stored = window.localStorage?.getItem(DO_NEXT_EXPANDED_STORAGE_KEY);
    if (stored === '0' || stored === 'false') return false;
    if (stored === '1' || stored === 'true') return true;
  } catch {
    // ignore storage errors
  }
  return true;
}

const BoardAiToolbar = ({
  editorRef,
  hashtag,
  activities,
  board,
  updatePlacementsLocal,
  savePlacements,
  saveFrames,
  saveSnapshot,
  deleteActivities,
  contextActions,
  contextLoading,
  contextError,
  thinkingPhase = '',
  lastJobMetrics = null,
  refreshContextActions,
  dismissNextAction,
  recordGapInterest,
  recordUserDecision,
}) => {
  const { startChatWithMessage, extensionId } = useBoardAiContext();
  const { extensionInstalled } = useSelector(state => state.api);
  const persistCurrentGoalProgress = useBoardStore(state => state.persistCurrentGoalProgress);
  const setCurrentStep = useBoardStore(state => state.setCurrentStep);
  const { geminiLanguage, selectedLabel: selectedGeminiLanguageLabel } = useGeminiLanguage();
  const { askProvider, askProviderLabel } = useAskProvider();
  const messages = useMemo(() => getCanvasMessages(geminiLanguage), [geminiLanguage]);
  const kindMeta = useMemo(() => {
    const next = {};
    for (const [kind, meta] of Object.entries(KIND_META)) {
      next[kind] = { ...meta, prefix: getKindPrefix(kind, geminiLanguage) };
    }
    return next;
  }, [geminiLanguage]);
  const doNextRefreshStatus = useAiThinkingStatus(contextLoading, 'context', geminiLanguage);
  const doNextRefreshBrief = useMemo(() => {
    if (!contextLoading) return '';
    const live = messages.thinkingLive || {};
    if (thinkingPhase === 'preparing') return live.preparing || doNextRefreshStatus;
    if (thinkingPhase === 'building') return live.building || doNextRefreshStatus;
    if (thinkingPhase === 'thinking') return live.thinking || doNextRefreshStatus;
    return doNextRefreshStatus || live.thinking || messages.refreshDoNext;
  }, [
    contextLoading,
    thinkingPhase,
    doNextRefreshStatus,
    messages.thinkingLive,
    messages.refreshDoNext,
  ]);
  const [loading, setLoading] = useState(false);
  const [loadingVariant, setLoadingVariant] = useState('command');
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [activeActionKey, setActiveActionKey] = useState(null);
  const [selectedBlockerIndex, setSelectedBlockerIndex] = useState(null);
  const [planAction, setPlanAction] = useState(null);
  const [planCustomAnswer, setPlanCustomAnswer] = useState('');
  /** After Goal decide/set-goal, show loading on Do next parts that refresh. */
  const [goalCoachRefresh, setGoalCoachRefresh] = useState(false);
  const [doneBlockerKeys, setDoneBlockerKeys] = useState(() => new Set());
  const [doneValidationKeys, setDoneValidationKeys] = useState(() => new Set());
  const [partialBlockerKeys, setPartialBlockerKeys] = useState(() => new Set());
  const [notInterestedBlockerKeys, setNotInterestedBlockerKeys] = useState(() => new Set());
  const [triedActionKeys, setTriedActionKeys] = useState(() => new Set());
  const [doNextExpanded, setDoNextExpanded] = useState(readDoNextExpanded);
  const doneKeysRef = useRef(new Set());
  const doneValidationKeysRef = useRef(new Set());
  const partialBlockerKeysRef = useRef(new Set());
  const notInterestedKeysRef = useRef(new Set());
  const triedActionKeysRef = useRef(new Set());

  const toggleDoNextExpanded = useCallback(() => {
    setDoNextExpanded(prev => {
      const next = !prev;
      try {
        window.localStorage?.setItem(DO_NEXT_EXPANDED_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  const runAiAction = useCallback(
    async (fetcher, {
      command: commandText,
      thinkingVariant = 'command',
      boardOverride,
      allowSuggestPins = false,
    } = {}) => {
      if (!hashtag || loading) return;
      setLoadingVariant(thinkingVariant);
      setLoading(true);
      setError(null);
      setStatus(null);
      try {
        const editor = editorRef.current;
        const clientContext = await buildClientContext(editor, {
          command: commandText,
          board: boardOverride || board,
        });
        const result = await fetcher(clientContext);
        if (!hasBoardAiMutations(result, { allowSuggestPins })) {
          if (result?.summary) {
            setStatus({ summary: result.summary, canUndo: false });
          } else {
            setError('AI finished but returned no layout changes. Try again.');
          }
          return;
        }

        const undoPayload = {
          placements: Array.isArray(board?.placements)
            ? board.placements.map(p => ({ ...p }))
            : [],
          frames: Array.isArray(board?.frames)
            ? board.frames.map(f => ({ ...f }))
            : [],
        };

        await applyBoardAiActions(editor, result, {
          activities,
          board,
          updatePlacementsLocal,
          savePlacements,
          saveFrames,
          saveSnapshot,
          deleteActivities,
          allowSuggestPins,
        });

        const summary = result.summary
          || (allowSuggestPins && result.suggestPins?.length
            ? `Added ${result.suggestPins.length} suggested pin${result.suggestPins.length === 1 ? '' : 's'}`
            : 'Board updated');
        setStatus({
          summary,
          canUndo: true,
          undoPayload,
        });
      } catch (err) {
        setError(err.message || 'AI request failed');
      } finally {
        setLoading(false);
      }
    },
    [
      hashtag,
      loading,
      editorRef,
      activities,
      board,
      updatePlacementsLocal,
      savePlacements,
      saveFrames,
      saveSnapshot,
      deleteActivities,
    ]
  );

  const handleUndoAiEdit = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const undoPayload = status?.undoPayload;
    try {
      editor.undo();
    } catch {
      /* ignore */
    }

    if (undoPayload) {
      const placements = undoPayload.placements || [];
      const frames = undoPayload.frames || [];
      // savePlacements owns local+API update; calling updatePlacementsLocal first
      // makes savePlacements no-op against the same payload.
      savePlacements?.(placements);
      saveFrames?.(frames);
      try {
        editor.run(() => {
          syncPinShapes(editor, activities, { ...board, placements, frames });
        });
        saveSnapshot?.(extractBoardDocumentSnapshot(editor));
      } catch {
        /* ignore */
      }
    }

    setStatus(null);
  }, [
    editorRef,
    status,
    activities,
    board,
    savePlacements,
    saveFrames,
    saveSnapshot,
  ]);

  const handleDismissStatus = useCallback(() => {
    setStatus(null);
    setError(null);
  }, []);

  const searchSuggestions = useMemo(() => {
    if (contextActions?.searchQueries?.length > 0) return contextActions.searchQueries;
    if (contextActions?.searchQuery) {
      return [{ query: contextActions.searchQuery, reason: contextActions.searchReason }];
    }
    return EMPTY_SEARCH_SUGGESTIONS;
  }, [
    contextActions?.searchQueries,
    contextActions?.searchQuery,
    contextActions?.searchReason,
  ]);
  const nextActions = contextActions?.nextActions?.length
    ? contextActions.nextActions
    : EMPTY_NEXT_ACTIONS;
  const blockers = useMemo(() => {
    const raw = Array.isArray(contextActions?.blockers) && contextActions.blockers.length
      ? contextActions.blockers
      : (contextActions?.gaps || [])
        .map(gap => (
          typeof gap === 'string'
            ? { title: gap, why: '', howToFill: '' }
            : gap
        ))
        .filter(item => item?.title);
    return dedupeBlockers(raw);
  }, [contextActions?.blockers, contextActions?.gaps]);
  const progressSummary = String(contextActions?.progressSummary || '').trim();
  const workingConclusion = String(contextActions?.workingConclusion || '').trim();
  const validationQueue = useMemo(
    () => (
      Array.isArray(contextActions?.validationQueue)
        ? contextActions.validationQueue
        : []
    ),
    [contextActions?.validationQueue]
  );
  const geminiQuestion = contextActions?.geminiQuestion;
  const geminiImageUrl = contextActions?.geminiImageUrl;
  const contextGaps = blockers.map(b => b.title).filter(Boolean);
  const suggestPinQueries = buildSuggestPinQueries({
    searchQueries: searchSuggestions,
    searchQuery: contextActions?.searchQuery,
    gaps: contextGaps,
  });
  const pinCount = useMemo(
    () => (activities || []).filter(a => getActivityId(a)).length,
    [activities]
  );
  const hasFlexibleNext = nextActions.length > 0;
  const hasRecommendations = hasFlexibleNext
    || searchSuggestions.length > 0
    || Boolean(contextActions?.geminiQuestion);
  // Keep prior still-missing + actions visible while a refresh / new-step fetch runs.
  // Goal decide/set-goal is the exception — show thinking on the changing Do next slot.
  const hasStaleCoachContent =
    blockers.length > 0 || hasRecommendations;
  const showThinkingInSlot = goalCoachRefresh
    || (contextLoading && !hasStaleCoachContent);
  const showRecommendationActions =
    !showThinkingInSlot && (hasStaleCoachContent || !contextLoading);
  const blockerActionLinks = useMemo(
    () => linkBlockersToActions({
      blockers,
      searchSuggestions,
      nextActions,
    }),
    [blockers, searchSuggestions, nextActions]
  );
  const linksByBlocker = useMemo(
    () => groupLinksByBlocker(blockerActionLinks),
    [blockerActionLinks]
  );
  const actionKeyMarks = useMemo(
    () => getActionKeyMarks(blockerActionLinks),
    [blockerActionLinks]
  );

  // Coach content (gaps / actions). Soft progress must not share this key —
  // done keys can update without regenerating recommendations.
  const coachContentFingerprint = useMemo(() => JSON.stringify({
    blockers: blockers.map(b => b.title),
    next: nextActions.map(a => [a.kind, a.label, a.query, a.question]),
    search: searchSuggestions.map(s => s.query),
    ask: contextActions?.geminiQuestion || '',
    summary: progressSummary || '',
    conclusion: workingConclusion || '',
    validation: validationQueue.map(v => [v.key, v.claim, v.probeKind]),
  }), [
    blockers,
    nextActions,
    searchSuggestions,
    contextActions?.geminiQuestion,
    progressSummary,
    workingConclusion,
    validationQueue,
  ]);

  // Status comes from the same step record the dropdown renders: a step marked
  // Done (by ticks, pin-case, the patch or the % slider) shows all gaps solved.
  const currentStepFinished = selectCurrentStep(board)?.status === 'finished';

  const progressFingerprint = useMemo(() => JSON.stringify({
    finished: currentStepFinished,
    rate: contextActions?.achievementRate ?? null,
    done: contextActions?.userProgress?.doneBlockerKeys || [],
    doneValidation: contextActions?.userProgress?.doneValidationKeys || [],
    doneFills: (contextActions?.userProgress?.doneFills || []).map(fill => [
      fill?.doneKey,
      fill?.shapeId,
      fill?.activityId,
    ]),
    notInterest: contextActions?.userProgress?.notInterestedKeys || [],
    partial: contextActions?.userProgress?.partialBlockerKeys || [],
    partialFills: (contextActions?.userProgress?.partialFills || []).map(fill => [
      fill?.partialKey,
      fill?.score,
    ]),
  }), [
    currentStepFinished,
    contextActions?.achievementRate,
    contextActions?.userProgress?.doneBlockerKeys,
    contextActions?.userProgress?.doneValidationKeys,
    contextActions?.userProgress?.doneFills,
    contextActions?.userProgress?.notInterestedKeys,
    contextActions?.userProgress?.partialBlockerKeys,
    contextActions?.userProgress?.partialFills,
  ]);

  const aiProgressBase = useMemo(() => {
    const fromUser = Number(contextActions?.userProgress?.aiAchievementRate);
    if (Number.isFinite(fromUser)) return fromUser;
    const fromRecs = Number(contextActions?.achievementRate);
    return Number.isFinite(fromRecs) ? fromRecs : 0;
  }, [contextActions?.userProgress?.aiAchievementRate, contextActions?.achievementRate]);

  const syncProgressFromRecommendations = useCallback((resetTried) => {
    const currentKeys = new Set(
      blockers.map((blocker, index) => blockerDoneKey(blocker, index))
    );
    const currentValidationKeys = new Set(
      validationQueue.map(item => String(item?.key || '').trim()).filter(Boolean)
    );
    const storedDone = Array.isArray(contextActions?.userProgress?.doneBlockerKeys)
      ? contextActions.userProgress.doneBlockerKeys
      : [];
    const storedValidationDone = Array.isArray(contextActions?.userProgress?.doneValidationKeys)
      ? contextActions.userProgress.doneValidationKeys
      : [];
    const storedNotInterest = Array.isArray(contextActions?.userProgress?.notInterestedKeys)
      ? contextActions.userProgress.notInterestedKeys
      : [];
    const persistedNotInterest = new Set(
      storedNotInterest.filter(key => currentKeys.has(key))
    );
    for (const raw of storedNotInterest) {
      const title = String(raw || '').trim().toLowerCase();
      if (!title) continue;
      blockers.forEach((blocker, index) => {
        const key = blockerDoneKey(blocker, index);
        if (
          key.toLowerCase() === title
          || String(blocker?.title || '').trim().toLowerCase() === title
        ) {
          persistedNotInterest.add(key);
        }
      });
    }
    const persistedDone = new Set(
      [...storedDone, ...persistedNotInterest]
        .filter(key => currentKeys.has(key))
    );
    // SoftProgress may store title-only keys; map them onto current blocker keys.
    const storedFillKeys = Array.isArray(contextActions?.userProgress?.doneFills)
      ? contextActions.userProgress.doneFills.map(fill => fill?.doneKey)
      : [];
    for (const raw of [...storedDone, ...storedFillKeys]) {
      const title = String(raw || '').trim().toLowerCase();
      if (!title) continue;
      blockers.forEach((blocker, index) => {
        const key = blockerDoneKey(blocker, index);
        if (
          key.toLowerCase() === title
          || String(blocker?.title || '').trim().toLowerCase() === title
        ) {
          persistedDone.add(key);
        }
      });
    }
    const persistedValidationDone = new Set(
      storedValidationDone
        .map(key => String(key || '').trim())
        .filter(key => {
          if (!key) return false;
          if (currentValidationKeys.has(key)) return true;
          return validationQueue.some(item => (
            String(item?.claim || '').trim().toLowerCase() === key.toLowerCase()
          ));
        })
    );
    for (const raw of storedValidationDone) {
      const needle = String(raw || '').trim().toLowerCase();
      if (!needle) continue;
      validationQueue.forEach(item => {
        const key = String(item?.key || '').trim();
        const claim = String(item?.claim || '').trim().toLowerCase();
        if (key && (key.toLowerCase() === needle || claim === needle)) {
          persistedValidationDone.add(key);
        }
      });
    }
    if (currentStepFinished) {
      // Done step: every remaining gap and probe counts as closed.
      for (const key of currentKeys) persistedDone.add(key);
      for (const key of currentValidationKeys) persistedValidationDone.add(key);
    }
    const storedPartial = Array.isArray(contextActions?.userProgress?.partialBlockerKeys)
      ? contextActions.userProgress.partialBlockerKeys
      : [];
    const persistedPartial = new Set(
      storedPartial
        .map(k => String(k || '').trim())
        .filter(key => currentKeys.has(key) && !persistedDone.has(key))
    );
    for (const raw of storedPartial) {
      const title = String(raw || '').trim().toLowerCase();
      if (!title) continue;
      blockers.forEach((blocker, index) => {
        const key = blockerDoneKey(blocker, index);
        if (
          (key.toLowerCase() === title || String(blocker?.title || '').trim().toLowerCase() === title)
          && !persistedDone.has(key)
        ) {
          persistedPartial.add(key);
        }
      });
    }
    partialBlockerKeysRef.current = persistedPartial;
    notInterestedKeysRef.current = persistedNotInterest;
    doneKeysRef.current = persistedDone;
    doneValidationKeysRef.current = persistedValidationDone;
    setPartialBlockerKeys(persistedPartial);
    setNotInterestedBlockerKeys(persistedNotInterest);
    setDoneBlockerKeys(persistedDone);
    setDoneValidationKeys(persistedValidationDone);
    if (resetTried) {
      triedActionKeysRef.current = new Set();
      setTriedActionKeys(new Set());
    } else {
      // Restore persisted "tried" destinations (e.g. after a page reload) without
      // clearing marks the user has set locally this session.
      const storedTried = Array.isArray(contextActions?.userProgress?.triedActionKeys)
        ? contextActions.userProgress.triedActionKeys
        : [];
      if (storedTried.length) {
        const persistedTried = new Set([...triedActionKeysRef.current, ...storedTried]);
        triedActionKeysRef.current = persistedTried;
        setTriedActionKeys(persistedTried);
      }
    }
  }, [
    blockers,
    validationQueue,
    currentStepFinished,
    contextActions?.userProgress?.doneBlockerKeys,
    contextActions?.userProgress?.doneValidationKeys,
    contextActions?.userProgress?.doneFills,
    contextActions?.userProgress?.notInterestedKeys,
    contextActions?.userProgress?.partialBlockerKeys,
    contextActions?.userProgress?.triedActionKeys,
  ]);

  // New coach gaps/actions: reseed progress and clear destination "tried" marks.
  useEffect(() => {
    syncProgressFromRecommendations(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on coach content change
  }, [coachContentFingerprint]);

  // Capture done+notInterested+partial count AFTER syncProgressFromRecommendations(true) has
  // pruned and reseeded the refs (effects run in declaration order, so this fires
  // after the sync above). This is the baseline for softProgressCount.
  const baseProgressCountRef = useRef(0);
  useEffect(() => {
    baseProgressCountRef.current = (
      doneKeysRef.current.size
      + notInterestedKeysRef.current.size
      + partialBlockerKeysRef.current.size
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only reset on coach content change
  }, [coachContentFingerprint]);

  // Pin-case soft progress / persisted %: reseed done chips without clearing tried.
  useEffect(() => {
    syncProgressFromRecommendations(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on progress change
  }, [progressFingerprint]);

  const displayedAchievementRate = useMemo(() => {
    const activeDone = blockers.reduce((count, blocker, index) => (
      doneBlockerKeys.has(blockerDoneKey(blocker, index)) ? count + 1 : count
    ), 0);
    const validationDoneCount = validationQueue.reduce((count, item) => (
      doneValidationKeys.has(String(item?.key || '').trim()) ? count + 1 : count
    ), 0);
    // Only show progress after the user has interacted — AI-only estimates must not
    // display a % chip just because a step was selected or a blocker chip was clicked.
    const hasPersistedUserDone =
      (Array.isArray(contextActions?.userProgress?.doneBlockerKeys)
        && contextActions.userProgress.doneBlockerKeys.length > 0)
      || (Array.isArray(contextActions?.userProgress?.doneValidationKeys)
        && contextActions.userProgress.doneValidationKeys.length > 0);
    if (!activeDone && !validationDoneCount && !hasPersistedUserDone) return null;
    const computed = computeGoalProgressRate(
      aiProgressBase,
      activeDone + validationDoneCount,
      blockers.length + validationQueue.length,
      { bumpPerBlocker: PROGRESS_BUMP_PER_BLOCKER, cap: PROGRESS_BUMP_CAP }
    );
    // While gaps hydrate, keep the persisted in-progress % so reload does not
    // flash back to the AI base rate.
    const stored = Number(contextActions?.achievementRate);
    if (
      activeDone === 0
      && validationDoneCount === 0
      && Number.isFinite(stored)
      && stored > computed
      && hasPersistedUserDone
    ) {
      return Math.min(PROGRESS_BUMP_CAP, stored);
    }
    return computed;
  }, [
    aiProgressBase,
    doneBlockerKeys,
    doneValidationKeys,
    blockers,
    validationQueue,
    contextActions?.achievementRate,
    contextActions?.userProgress?.doneBlockerKeys,
    contextActions?.userProgress?.doneValidationKeys,
  ]);

  const persistProgressSnapshot = useCallback((
    nextDoneKeys,
    nextNotInterestedKeys,
    nextDoneValidationKeys = doneValidationKeysRef.current,
    nextTriedActionKeys = triedActionKeysRef.current,
    { removeDoneKeys = [] } = {}
  ) => {
    if (!readCurrentStep(board)) return;
    const activeDone = blockers.reduce((count, blocker, index) => (
      nextDoneKeys.has(blockerDoneKey(blocker, index)) ? count + 1 : count
    ), 0);
    const validationDoneCount = validationQueue.reduce((count, item) => (
      nextDoneValidationKeys.has(String(item?.key || '').trim()) ? count + 1 : count
    ), 0);
    const status = resolveGoalStepStatus(
      { blockers, validationQueue },
      {
        doneBlockerKeys: [...nextDoneKeys],
        notInterestedKeys: [...nextNotInterestedKeys],
        doneValidationKeys: [...nextDoneValidationKeys],
      }
    );
    // In-progress gap clicks bump toward 95 so we never claim the step is
    // complete from a single mark. All gaps closed → the step is done: 100.
    const nextRate = status === 'finished'
      ? 100
      : computeGoalProgressRate(
        aiProgressBase,
        activeDone + validationDoneCount,
        blockers.length + validationQueue.length,
        { bumpPerBlocker: PROGRESS_BUMP_PER_BLOCKER, cap: PROGRESS_BUMP_CAP }
      );
    void persistCurrentGoalProgress({
      achievementRate: nextRate,
      doneBlockerKeys: [...nextDoneKeys],
      doneValidationKeys: [...nextDoneValidationKeys],
      notInterestedKeys: [...nextNotInterestedKeys],
      triedActionKeys: [...nextTriedActionKeys],
      aiAchievementRate: aiProgressBase,
      pinCount,
      locale: geminiLanguage,
      status,
      removeDoneKeys,
    });
  }, [
    readCurrentStep(board),
    blockers,
    validationQueue,
    aiProgressBase,
    persistCurrentGoalProgress,
    pinCount,
    geminiLanguage,
  ]);

  // Step dropdown changes currentStep + coach pack — re-pick the priority gap
  // instead of keeping the previous step's selectedBlockerIndex.
  const activeGoalKey = readCurrentStep(board).trim().toLowerCase();
  const prevActiveGoalKeyRef = useRef(activeGoalKey);
  const goalJustChanged = prevActiveGoalKeyRef.current !== activeGoalKey;

  useEffect(() => {
    prevActiveGoalKeyRef.current = activeGoalKey;
  }, [activeGoalKey]);

  useEffect(() => {
    if (!blockers.length) {
      setSelectedBlockerIndex(null);
      setActiveActionKey(null);
      return;
    }

    setSelectedBlockerIndex(prev => {
      const prevValid = prev != null && prev >= 0 && prev < blockers.length;
      if (goalJustChanged || !prevValid) {
        const next = findPriorityBlockerIndex(
          blockers,
          doneKeysRef.current,
          notInterestedKeysRef.current,
          linksByBlocker
        );
        return next >= 0 ? next : (prevValid ? prev : null);
      }

      const prevKey = blockerDoneKey(blockers[prev], prev);
      const prevSolved = doneKeysRef.current.has(prevKey);
      const prevNotInterested = notInterestedKeysRef.current.has(prevKey);
      // Not interested: keep selection so the user can undo from the same row.
      if (prevNotInterested) return prev;
      // Solved: advance to the next open missing part (or stay if none left).
      if (prevSolved) {
        const next = findPriorityBlockerIndex(
          blockers,
          doneKeysRef.current,
          notInterestedKeysRef.current,
          linksByBlocker
        );
        return next >= 0 ? next : prev;
      }
      return prev;
    });
  }, [
    blockers,
    doneBlockerKeys,
    notInterestedBlockerKeys,
    linksByBlocker,
    activeGoalKey,
    goalJustChanged,
  ]);

  // When nothing is actively highlighted, point at the priority step for the
  // currently selected open gap (or the first fallback next-action).
  useEffect(() => {
    if (contextLoading) return;

    if (selectedBlockerIndex != null
      && selectedBlockerIndex >= 0
      && selectedBlockerIndex < blockers.length) {
      const links = linksByBlocker.get(selectedBlockerIndex) || [];
      setActiveActionKey(prev => {
        // Across steps, action keys like "ask" collide — always take the new pack.
        if (goalJustChanged) return links[0]?.actionKey || null;
        if (prev && links.some(link => link.actionKey === prev)) return prev;
        return links[0]?.actionKey || null;
      });
      return;
    }

    if (!blockers.length && nextActions.length) {
      const top = [...nextActions].sort((a, b) => (a.priority || 99) - (b.priority || 99))[0];
      const key = actionKeyForNextAction(top, nextActions.indexOf(top));
      setActiveActionKey(prev => (goalJustChanged ? key : (prev || key)));
      return;
    }

    setActiveActionKey(null);
  }, [
    contextLoading,
    selectedBlockerIndex,
    blockers,
    linksByBlocker,
    nextActions,
    doneBlockerKeys,
    notInterestedBlockerKeys,
    activeGoalKey,
    goalJustChanged,
  ]);

  const closePlan = useCallback(() => {
    setPlanAction(null);
    setPlanCustomAnswer('');
  }, []);

  useEffect(() => {
    if (!contextLoading) setGoalCoachRefresh(false);
  }, [contextLoading]);

  const boardGoalRefreshToken = useBoardStore(state => state.boardGoalRefreshToken || 0);
  const seenBoardGoalRefreshTokenRef = useRef(boardGoalRefreshToken);
  useEffect(() => {
    if (seenBoardGoalRefreshTokenRef.current === boardGoalRefreshToken) return;
    seenBoardGoalRefreshTokenRef.current = boardGoalRefreshToken;
    setDoNextExpanded(true);
    setGoalCoachRefresh(true);
  }, [boardGoalRefreshToken]);

  const markBlockerAttempted = useCallback((blockerIndex, { persist = true } = {}) => {
    if (blockerIndex == null || blockerIndex < 0 || blockerIndex >= blockers.length) {
      return;
    }
    const key = blockerDoneKey(blockers[blockerIndex], blockerIndex);
    const alreadyDone = doneKeysRef.current.has(key);
    doneKeysRef.current.add(key);
    setDoneBlockerKeys(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (persist && !alreadyDone) {
      persistProgressSnapshot(doneKeysRef.current, notInterestedKeysRef.current);
    }
  }, [blockers, persistProgressSnapshot]);

  const markValidationDone = useCallback((validationKey, { persist = true } = {}) => {
    const key = String(validationKey || '').trim();
    if (!key) return;
    const alreadyDone = doneValidationKeysRef.current.has(key);
    doneValidationKeysRef.current.add(key);
    setDoneValidationKeys(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (persist && !alreadyDone) {
      persistProgressSnapshot(
        doneKeysRef.current,
        notInterestedKeysRef.current,
        doneValidationKeysRef.current
      );
    }
  }, [persistProgressSnapshot]);

  /** Manual done / undo: toggle the selected missing part solved state. */
  const toggleSelectedBlockerSolved = useCallback(() => {
    if (selectedBlockerIndex == null || selectedBlockerIndex < 0
      || selectedBlockerIndex >= blockers.length) {
      return;
    }
    const blocker = blockers[selectedBlockerIndex];
    const key = blockerDoneKey(blocker, selectedBlockerIndex);
    if (doneKeysRef.current.has(key)) {
      doneKeysRef.current.delete(key);
      setDoneBlockerKeys(prev => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      // Undo must replace server-side: a plain progress write only unions keys.
      persistProgressSnapshot(
        doneKeysRef.current,
        notInterestedKeysRef.current,
        doneValidationKeysRef.current,
        triedActionKeysRef.current,
        { removeDoneKeys: [key, String(blocker?.title || '').trim()] }
      );
      return;
    }
    markBlockerAttempted(selectedBlockerIndex, { persist: true });
  }, [
    blockers,
    selectedBlockerIndex,
    markBlockerAttempted,
    persistProgressSnapshot,
  ]);

  const toggleSelectedBlockerNotInterested = useCallback(() => {
    if (selectedBlockerIndex == null || selectedBlockerIndex < 0
      || selectedBlockerIndex >= blockers.length) {
      return;
    }
    const blocker = blockers[selectedBlockerIndex];
    const key = blockerDoneKey(blocker, selectedBlockerIndex);
    const title = String(blocker?.title || '').trim() || key;
    const currentlyNotInterested = notInterestedKeysRef.current.has(key);

    if (currentlyNotInterested) {
      notInterestedKeysRef.current.delete(key);
      doneKeysRef.current.delete(key);
      setNotInterestedBlockerKeys(prev => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setDoneBlockerKeys(prev => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      // Undo must replace server-side: a plain progress write only unions keys.
      persistProgressSnapshot(
        doneKeysRef.current,
        notInterestedKeysRef.current,
        doneValidationKeysRef.current,
        triedActionKeysRef.current,
        { removeDoneKeys: [key, title] }
      );
      recordGapInterest?.(title, { interested: true });
      return;
    }

    notInterestedKeysRef.current.add(key);
    setNotInterestedBlockerKeys(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    // Mark done for progress; keep this gap selected so actions stay in view.
    markBlockerAttempted(selectedBlockerIndex, { persist: false });
    persistProgressSnapshot(doneKeysRef.current, notInterestedKeysRef.current);
    recordGapInterest?.(title, { interested: false });
  }, [
    blockers,
    selectedBlockerIndex,
    markBlockerAttempted,
    persistProgressSnapshot,
    recordGapInterest,
  ]);

  const highlightBlockerOnCanvas = useCallback((blocker) => {
    const editor = editorRef?.current;
    if (!editor || !blocker) return;
    const pinIds = Array.isArray(blocker.exemplarPinIds) ? blocker.exemplarPinIds : [];
    const shapeIds = [];
    for (const activityId of pinIds) {
      const desired = `shape:pin-${activityId}`;
      if (editor.getShape(desired)) {
        shapeIds.push(desired);
        continue;
      }
      const match = editor.getCurrentPageShapes?.().find(
        s => s.type === 'pin-card' && String(s.meta?.activityId) === String(activityId)
      );
      if (match?.id) shapeIds.push(match.id);
    }
    for (const clusterId of blocker.clusterIds || []) {
      const raw = String(clusterId || '');
      if (!raw.startsWith('frame:')) continue;
      const logicalId = raw.slice('frame:'.length);
      const frameShape = editor.getCurrentPageShapes?.().find(
        s => s.type === 'frame'
          && (String(s.meta?.boardFrameId) === logicalId
            || String(s.id).includes(logicalId))
      );
      if (frameShape?.id) shapeIds.push(frameShape.id);
    }
    if (!shapeIds.length) return;
    editor.setSelectedShapes(shapeIds);
    try {
      editor.zoomToSelection?.({ animation: { duration: 200 } });
    } catch {
      // ignore camera helpers missing in tests
    }
  }, [editorRef]);

  /** Collect shape IDs on canvas for all fills (done + partial) of a blocker key. */
  const collectFillShapeIds = useCallback((blockerKey, doneFills = [], partialFills = []) => {
    const editor = editorRef?.current;
    if (!editor || !blockerKey) return [];
    const keyLower = blockerKey.toLowerCase();
    const shapeIds = [];

    const resolveActivityId = (activityId) => {
      const desired = `shape:pin-${activityId}`;
      if (editor.getShape(desired)) return desired;
      const match = editor.getCurrentPageShapes?.().find(
        s => (s.type === 'pin-card' || s.type === 'suggest-pin')
          && String(s.meta?.activityId) === String(activityId)
      );
      return match?.id || null;
    };

    for (const fill of doneFills || []) {
      if (String(fill?.doneKey || '').trim().toLowerCase() !== keyLower) continue;
      const activityId = String(fill?.activityId || '').trim();
      const shapeId = String(fill?.shapeId || '').trim();
      if (activityId) {
        const sid = resolveActivityId(activityId);
        if (sid) shapeIds.push(sid);
      } else if (shapeId && editor.getShape(shapeId)) {
        shapeIds.push(shapeId);
      }
    }

    for (const fill of partialFills || []) {
      if (String(fill?.partialKey || '').trim().toLowerCase() !== keyLower) continue;
      const activityId = String(fill?.activityId || '').trim();
      const shapeId = String(fill?.shapeId || '').trim();
      if (activityId) {
        const sid = resolveActivityId(activityId);
        if (sid) shapeIds.push(sid);
      } else if (shapeId && editor.getShape(shapeId)) {
        shapeIds.push(shapeId);
      }
    }

    return [...new Set(shapeIds)];
  }, [editorRef]);

  const markFallbackActionTried = useCallback((actionKey, { persist = true } = {}) => {
    const key = String(actionKey || '').trim();
    if (!key) return false;
    if (triedActionKeysRef.current.has(key)) return false;
    const next = new Set(triedActionKeysRef.current);
    next.add(key);
    triedActionKeysRef.current = next;
    setTriedActionKeys(next);
    if (persist) {
      // Persist immediately so "tried" destinations survive a reload and can
      // inform the next AI regeneration (avoid resuggesting the same search/ask).
      persistProgressSnapshot(doneKeysRef.current, notInterestedKeysRef.current);
    }
    return true;
  }, [persistProgressSnapshot]);

  /**
   * Record that a recommended action was tried (search/ask/etc).
   * Missing parts become done only via pin-case, Solved, or Not interested —
   * not from clicking an action button.
   */
  const markProgressForAction = useCallback((actionKey) => {
    markFallbackActionTried(actionKey, { persist: true });
  }, [markFallbackActionTried]);

  const afterSearchOrAskDestination = useCallback((actionKey, action = null) => {
    markProgressForAction(actionKey, action);
  }, [markProgressForAction]);

  const collectRelatedAskItems = useCallback((question, action = null) => (
    collectRelatedAskContextItems(editorRef?.current, {
      activities,
      question,
      action,
      blockers,
      board,
    })
  ), [editorRef, activities, blockers, board]);

  const openAsk = useCallback((question, action = null) => {
    const prompt = buildAskPrompt({
      question,
      languageLabel: selectedGeminiLanguageLabel,
    });
    startChatWithMessage?.(prompt, {
      newConversation: true,
      reuseQuestion: question,
      contextItems: collectRelatedAskItems(question, action),
    });
  }, [
    selectedGeminiLanguageLabel,
    startChatWithMessage,
    collectRelatedAskItems,
  ]);

  const openSearch = useCallback((query) => {
    const trimmed = String(query || '').trim();
    if (!trimmed) return;
    const prompt = buildSearchPrompt({
      query: trimmed,
      languageLabel: selectedGeminiLanguageLabel,
    });
    startChatWithMessage?.(prompt, { newConversation: true });
  }, [
    selectedGeminiLanguageLabel,
    startChatWithMessage,
  ]);

  const runValidationProbe = useCallback((item) => {
    if (!item) return;
    if (item.probeKind === 'ask' && item.question) {
      openAsk(item.question);
    } else if (item.query) {
      openSearch(item.query);
    }
    markValidationDone(item.key, { persist: true });
  }, [openAsk, openSearch, markValidationDone]);

  const handleOpenGoogleSearch = useCallback(async (query) => {
    const trimmed = String(query || '').trim();
    if (!trimmed) return false;
    if (!extensionInstalled) {
      const proceed = await boardConfirm({
        title: 'Extension required',
        message:
          'Install the Chrome extension to pin what you find. You can continue to Google Search without it.',
        confirmLabel: 'Continue to Google',
        cancelLabel: 'Cancel',
        href: EXTENSION_URL,
        hrefLabel: 'Install the Hashely extension',
      });
      if (!proceed) return false;
    }
    openGoogleSearch(trimmed);
    return true;
  }, [extensionInstalled]);

  const handleOpenExternalAsk = useCallback(async (question, action = null) => {
    const trimmed = String(question || '').trim();
    if (!trimmed) return false;
    if (!extensionInstalled) {
      const proceed = await boardConfirm({
        title: 'Extension required',
        message: messages.extensionRequiredForProvider(askProviderLabel),
        confirmLabel: messages.continueToProvider(askProviderLabel),
        cancelLabel: 'Cancel',
        href: EXTENSION_URL,
        hrefLabel: 'Install the Hashely extension',
      });
      if (!proceed) return false;
    }
    const prompt = buildExternalAskPrompt(trimmed, [], {
      editor: editorRef?.current,
      board,
      action,
      constraints: board?.contextRecommendations?.constraints,
    });
    openExternalAsk(prompt || trimmed, {
      provider: askProvider,
      extensionId,
      extensionInstalled,
      language: geminiLanguage,
    });
    return true;
  }, [
    extensionInstalled,
    extensionId,
    geminiLanguage,
    askProvider,
    askProviderLabel,
    messages,
    board,
    editorRef,
  ]);

  const runSearchInApp = useCallback((query, actionKey, action = null) => {
    openSearch(query);
    afterSearchOrAskDestination(actionKey, action);
    return true;
  }, [openSearch, afterSearchOrAskDestination]);

  const runSearchGoogle = useCallback(async (query, actionKey, action = null) => {
    const opened = await handleOpenGoogleSearch(query);
    if (opened) afterSearchOrAskDestination(actionKey, action);
    return opened;
  }, [handleOpenGoogleSearch, afterSearchOrAskDestination]);

  const runAskInApp = useCallback((question, actionKey, action = null) => {
    openAsk(question, action);
    afterSearchOrAskDestination(actionKey, action);
    return true;
  }, [openAsk, afterSearchOrAskDestination]);

  const runAskOuter = useCallback(async (question, actionKey, action = null) => {
    const opened = await handleOpenExternalAsk(question, action);
    if (opened) afterSearchOrAskDestination(actionKey, action);
    return opened;
  }, [handleOpenExternalAsk, afterSearchOrAskDestination]);

  const handlePlaceOnBoard = useCallback((queries = suggestPinQueries) => {
    const pinQueries = (Array.isArray(queries) ? queries : []).filter(Boolean);
    if (!pinQueries.length || loading) return;
    runAiAction(ctx => postBoardAiSuggestPins(hashtag, pinQueries, ctx), {
      thinkingVariant: 'analyze',
      allowSuggestPins: true,
    });
  }, [loading, runAiAction, hashtag, suggestPinQueries]);

  const handleAddText = useCallback((action) => {
    const note = String(action?.text || action?.label || '').trim();
    if (!note) return;
    // Never auto-draw notes on the canvas from AI recommendations.
    openAsk(note);
  }, [openAsk]);

  const handleSetGoal = useCallback(async (goalText, actionToDismiss = null) => {
    const trimmed = String(goalText || '').trim();
    if (!trimmed) return;
    closePlan();
    if (actionToDismiss) dismissNextAction?.(actionToDismiss);
    setDoNextExpanded(true);
    // Steps are never typed in: an existing step only moves the pointer,
    // anything else asks for a fresh list.
    const existing = findStep(board, trimmed);
    if (existing) {
      await setCurrentStep({ stepId: existing.id });
      return;
    }
    refreshContextActions?.();
  }, [closePlan, dismissNextAction, board, setCurrentStep, refreshContextActions]);

  const handleDecideAnswer = useCallback(async (
    action,
    answer,
    option = null,
    { destination = null } = {},
  ) => {
    const choice = String(answer || option?.label || '').trim();
    if (!choice) return;

    const optionKind = resolvePlanActionKind(option, {
      hasCustomAnswer: !option,
      actionKind: action?.kind || '',
    });
    const destKey = `plan:${optionKind}:${choice}`;
    const isDecideJob = (action?.kind || 'decide') === 'decide';

    const announceFinish = (summary) => {
      const dismissKey = actionKeyForNextAction(action);
      markProgressForAction(dismissKey, action);
      const remaining = nextActions.filter((item, index) => {
        const itemKey = actionKeyForNextAction(item, index);
        if (dismissKey && itemKey === dismissKey) return false;
        if (item === action) return false;
        return true;
      });
      const followUp = remaining[0]
        ? messages.decideFollowUp(briefNextActionLabel(remaining[0], geminiLanguage))
        : '';
      const parts = [summary, followUp].filter(Boolean);
      if (parts.length) {
        setStatus({ summary: parts.join(' · '), canUndo: false });
      }
      if (remaining[0]) {
        const remainKey = actionKeyForNextAction(
          remaining[0],
          nextActions.indexOf(remaining[0]),
        );
        setActiveActionKey(remainKey);
        setDoNextExpanded(true);
      }
      dismissNextAction?.(action);
      closePlan();
    };

    const commitDecision = async () => {
      const question = action?.question || action?.label || '';
      const shapeId = placeDecisionTextOnBoard(editorRef.current, {
        question,
        answer: choice,
        activities,
        saveSnapshot,
      });
      await recordUserDecision?.({
        question,
        answer: choice,
        blockerTitle: action?.blockerTitle || '',
        sourceShapeId: shapeId || '',
      });
      announceFinish(messages.decideNoted(choice));
      setDoNextExpanded(true);
      setGoalCoachRefresh(true);
      refreshContextActions?.({ userDecision: true });
    };

    // Decide jobs record the choice on the board + ledger, then refresh later
    // steps. Do not hijack the click into Ask/Search just because the model
    // tagged the option with those kinds.
    if (isDecideJob) {
      await commitDecision();
      return;
    }

    const runAskDestination = async (question, summary) => {
      const prompt = String(question || '').trim();
      if (!prompt) return;
      // Search/Ask must pick in-app vs external first — never auto-run chat.
      if (!destination) return;
      const opened = destination === 'external'
        ? await runAskOuter(prompt, destKey)
        : runAskInApp(prompt, destKey);
      if (opened) announceFinish(summary || messages.decideOpenedAsk);
    };

    await recordUserDecision?.({
      question: action?.question || action?.label || '',
      answer: choice,
      blockerTitle: action?.blockerTitle || '',
    });

    if (optionKind === 'search' && (option?.query || choice)) {
      if (!destination) return;
      const query = option?.query || choice;
      const opened = destination === 'external'
        ? await runSearchGoogle(query, destKey)
        : runSearchInApp(query, destKey);
      if (opened) announceFinish(messages.decideOpenedSearch);
      return;
    }
    if (optionKind === 'ask') {
      const optionQ = String(option?.question || '').trim();
      const decideQ = String(action?.question || action?.label || '').trim();
      // option.question must be an AI synthesis question, not the user-facing
      // decide prompt. If the model copied/omitted it, just record the choice.
      const isDistinctSynthesisQ = optionQ && optionQ.toLowerCase() !== decideQ.toLowerCase();
      if (!isDistinctSynthesisQ) {
        announceFinish(messages.decideNoted(choice));
        return;
      }
      await runAskDestination(optionQ, messages.decideOpenedAsk);
      return;
    }
    if (optionKind === 'place') {
      handlePlaceOnBoard(option?.query ? [option.query] : action?.pinQueries);
      announceFinish(messages.decideNoted(choice));
      return;
    }
    if (optionKind === 'set-goal') {
      const goalText = String(option?.goal || choice || '').trim();
      await handleSetGoal(goalText, action);
      const remaining = nextActions.filter((item, index) => {
        const itemKey = actionKeyForNextAction(item, index);
        const dismissKey = actionKeyForNextAction(action);
        if (dismissKey && itemKey === dismissKey) return false;
        if (item === action) return false;
        return true;
      });
      const followUp = remaining[0]
        ? messages.decideFollowUp(briefNextActionLabel(remaining[0], geminiLanguage))
        : '';
      const parts = [messages.decideSetGoal(goalText), followUp].filter(Boolean);
      if (parts.length) {
        setStatus({ summary: parts.join(' · '), canUndo: false });
      }
      if (remaining[0]) {
        setActiveActionKey(actionKeyForNextAction(
          remaining[0],
          nextActions.indexOf(remaining[0]),
        ));
        setDoNextExpanded(true);
      }
      return;
    }
    if (optionKind === 'add-text') {
      announceFinish(messages.decideNoted(choice));
      return;
    }

    announceFinish(messages.decideNoted(choice));
  }, [
    closePlan,
    dismissNextAction,
    markProgressForAction,
    recordUserDecision,
    refreshContextActions,
    runAskInApp,
    runAskOuter,
    runSearchInApp,
    runSearchGoogle,
    handlePlaceOnBoard,
    handleSetGoal,
    nextActions,
    messages,
    geminiLanguage,
    editorRef,
    activities,
    saveSnapshot,
  ]);

  const runNextAction = useCallback((action) => {
    if (!action || loading || contextLoading) return;
    const kind = action.kind || 'decide';
    const key = actionKeyForNextAction(action);

    if (kind === 'search') {
      const query = String(action.query || '').trim();
      if (query) runSearchInApp(query, key, action);
      return;
    }
    if (kind === 'ask') {
      runAskInApp(action.question || action.label, key, action);
      return;
    }
    if (kind === 'place') {
      const queries = action.pinQueries?.length ? action.pinQueries : suggestPinQueries;
      handlePlaceOnBoard(queries);
      markProgressForAction(key, action);
      return;
    }
    if (kind === 'add-text') {
      handleAddText(action);
      markProgressForAction(key, action);
      return;
    }
    if (kind === 'set-goal') {
      if (needsPlanModal(action)) {
        setPlanAction(action);
        setPlanCustomAnswer('');
        return;
      }
      void handleSetGoal(action.goal || action.label, action);
      return;
    }

    // decide (default): open a modal for interactive planning
    setPlanAction(action);
    setPlanCustomAnswer('');
  }, [
    loading,
    contextLoading,
    runAskInApp,
    runSearchInApp,
    handlePlaceOnBoard,
    suggestPinQueries,
    handleAddText,
    handleSetGoal,
    markProgressForAction,
  ]);

  const isActionHighlighted = useCallback(actionKey => (
    Boolean(actionKey) && activeActionKey === actionKey
  ), [activeActionKey]);

  const actionClassName = useCallback((baseClass, actionKey) => (
    [
      baseClass,
      isActionHighlighted(actionKey) ? 'is-blocker-linked-active' : '',
    ].filter(Boolean).join(' ')
  ), [isActionHighlighted]);

  const renderDestinationGroup = ({
    kind,
    actionKey,
    label,
    fullLabel,
    marks = [],
    imageUrl,
    Icon,
    inAppLabel,
    externalLabel,
    ExternalIcon = null,
    onInApp,
    onExternal,
    disabled = false,
    tried = false,
  }) => {
    const showTooltip = Boolean(fullLabel) && isActionLabelTruncated(
      String(fullLabel || '').replace(new RegExp(`^${kind === 'ask' ? messages.askPrefix : messages.searchPrefix}\\s*:\\s*`, 'i'), '')
    );
    const group = (
      <Box
        className={[
          'board-ai-dest-group',
          kind === 'search' ? 'board-ai-dest-group--search' : 'board-ai-dest-group--ask',
          tried ? 'is-dest-tried' : '',
          isActionHighlighted(actionKey) ? 'is-blocker-linked-active' : '',
        ].filter(Boolean).join(' ')}
        onMouseEnter={() => setActiveActionKey(actionKey)}
        onMouseLeave={() => setActiveActionKey(null)}
      >
        <Box className="board-ai-dest-label-row">
          {imageUrl
            ? <RecommendationThumb imageUrl={imageUrl} fallbackIcon={Icon} />
            : (Icon ? <Icon fontSize="small" size={16} /> : null)}
          <ActionLinkMarks marks={marks} />
          <span className="board-ai-dest-label">{label}</span>
          {tried ? (
            <CheckIcon className="board-ai-dest-tried-mark" fontSize="small" titleAccess={messages.actionTried} />
          ) : null}
        </Box>
        <Box className="board-ai-dest-buttons">
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={() => onInApp?.()}
          >
            {inAppLabel}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            className={ExternalIcon ? 'board-ai-dest-external-btn' : undefined}
            startIcon={ExternalIcon ? <ExternalIcon size={14} /> : undefined}
            onClick={() => onExternal?.()}
          >
            {externalLabel}
          </Button>
          <HowToPinButton />
        </Box>
      </Box>
    );
    return showTooltip ? (
      <Tooltip key={actionKey} title={fullLabel} {...RECOMMENDATION_TOOLTIP_PROPS}>
        <span className="board-ai-context-action-btn-tooltip-wrap board-ai-dest-wrap">{group}</span>
      </Tooltip>
    ) : (
      <span key={actionKey} className="board-ai-context-action-btn-tooltip-wrap board-ai-dest-wrap">{group}</span>
    );
  };

  const renderSearchButton = (item, { mark, marks: marksProp, actionKey } = {}) => {
    const query = item?.query || '';
    const labelCore = truncateActionLabel(query);
    const fullText = query ? `${messages.searchPrefix}: ${query}` : '';
    const key = actionKey || `search:${query}`;
    const marks = marksProp
      || (mark != null ? [mark] : (actionKeyMarks.get(key) || []));
    return renderDestinationGroup({
      kind: 'search',
      actionKey: key,
      label: labelCore ? `${messages.searchPrefix}: ${labelCore}` : messages.search,
      fullLabel: fullText,
      marks,
      imageUrl: item?.imageUrl,
      Icon: SearchIcon,
      inAppLabel: messages.searchInApp,
      externalLabel: messages.searchGoogle,
      ExternalIcon: GoogleIcon,
      onInApp: () => runSearchInApp(query, key, item),
      onExternal: () => { void runSearchGoogle(query, key, item); },
      disabled: loading || contextLoading || !query,
      tried: triedActionKeys.has(key),
    });
  };

  const renderAskButton = ({
    question = geminiQuestion,
    label,
    imageUrl = geminiImageUrl,
    mark,
    marks: marksProp,
    actionKey = 'ask',
    blockerTitle = '',
  } = {}) => {
    const askText = String(question || '').trim();
    const labelCore = truncateActionLabel(label || askText);
    const fullText = askText ? `${messages.askPrefix}: ${askText}` : '';
    const key = actionKey;
    const marks = marksProp
      || (mark != null ? [mark] : (actionKeyMarks.get(key) || []));
    const ProviderIcon = (props) => (
      <AskProviderIcon provider={askProvider} size={16} {...props} />
    );
    // MUI SvgIcon API uses fontSize; brand icons use size.
    ProviderIcon.muiName = 'SvgIcon';

    return renderDestinationGroup({
      kind: 'ask',
      actionKey: key,
      label: labelCore ? `${messages.askPrefix}: ${labelCore}` : messages.askGemini,
      fullLabel: fullText,
      marks,
      imageUrl,
      Icon: ProviderIcon,
      inAppLabel: messages.askInApp,
      externalLabel: messages.askOuterProvider(askProviderLabel),
      ExternalIcon: (props) => (
        <AskProviderStartIcon provider={askProvider} size={14} {...props} />
      ),
      onInApp: () => runAskInApp(askText, key, { question: askText, blockerTitle }),
      onExternal: () => { void runAskOuter(askText, key, { question: askText, blockerTitle }); },
      disabled: loading || contextLoading || !askText,
      tried: triedActionKeys.has(key),
    });
  };

  const renderNextActionButton = (action, index) => {
    const kind = action?.kind || 'decide';
    if (kind === 'organize') return null;
    const meta = kindMeta[kind] || kindMeta.decide;
    const Icon = meta.Icon;
    const key = actionKeyForNextAction(action, index);
    const marks = actionKeyMarks.get(key) || [];

    if (kind === 'search') {
      const query = String(action.query || '').trim();
      // Skip rendering as a search button when there's no actual search query —
      // falling back to the label would show a procedural description as keywords.
      if (query) {
        return renderSearchButton(
          { query, reason: action.why, imageUrl: action.imageUrl },
          { actionKey: key, marks }
        );
      }
    }
    if (kind === 'ask') {
      const question = String(action.question || action.label || '').trim();
      return renderAskButton({
        question,
        label: action.label,
        imageUrl: action.imageUrl || geminiImageUrl,
        actionKey: key,
        marks,
      });
    }

    const labelCore = truncateActionLabel(action.label);
    const display = `${meta.prefix}: ${labelCore}`;
    const detail = [action.why, action.question, action.text, action.query]
      .filter(Boolean)
      .join(' · ');
    const showTooltip = Boolean(detail) || isActionLabelTruncated(action.label);
    const disabled = loading || contextLoading;

    const button = (
      <Button
        size="small"
        variant="outlined"
        disabled={disabled}
        startIcon={<Icon fontSize="small" />}
        onClick={() => runNextAction(action)}
        onMouseEnter={() => setActiveActionKey(key)}
        onMouseLeave={() => setActiveActionKey(null)}
        className={actionClassName(
          [
            'board-ai-context-action-btn',
            kind === 'decide' ? 'board-ai-decide-btn' : '',
            kind === 'add-text' ? 'board-ai-add-text-btn' : '',
            kind === 'set-goal' ? 'board-ai-set-goal-btn' : '',
          ].filter(Boolean).join(' '),
          key
        )}
      >
        <ActionLinkMarks marks={marks} />
        <span className="board-ai-context-action-label">{display}</span>
      </Button>
    );

    return showTooltip ? (
      <Tooltip key={key} title={detail || action.label} {...RECOMMENDATION_TOOLTIP_PROPS}>
        <span className="board-ai-context-action-btn-tooltip-wrap">{button}</span>
      </Tooltip>
    ) : (
      <React.Fragment key={key}>{button}</React.Fragment>
    );
  };

  const renderLinkedStep = (link, index) => {
    if (!link) return null;
    if (link.nextAction) {
      const actionIndex = nextActions.indexOf(link.nextAction);
      return renderNextActionButton(
        link.nextAction,
        actionIndex >= 0 ? actionIndex : index
      );
    }
    if (link.actionKind === 'search' && link.searchQuery) {
      return renderSearchButton(
        {
          query: link.searchQuery,
          reason: link.reason,
          imageUrl: link.imageUrl,
          blockerTitle: link.blocker?.title || '',
        },
        { mark: link.mark, actionKey: link.actionKey }
      );
    }
    if (link.actionKind === 'ask') {
      return renderAskButton({
        question: link.question || geminiQuestion,
        mark: link.mark,
        actionKey: link.actionKey || 'ask',
        blockerTitle: link.blocker?.title || '',
      });
    }
    return null;
  };

  const selectedLinks = selectedBlockerIndex != null
    ? (linksByBlocker.get(selectedBlockerIndex) || [])
    : [];
  const selectedBlocker = selectedBlockerIndex != null
    ? blockers[selectedBlockerIndex]
    : null;
  const selectedBlockerKey = selectedBlocker
    ? blockerDoneKey(selectedBlocker, selectedBlockerIndex)
    : null;
  const selectedBlockerNotInterested = Boolean(
    selectedBlockerKey && notInterestedBlockerKeys.has(selectedBlockerKey)
  );
  const selectedBlockerDone = Boolean(
    selectedBlockerKey && doneBlockerKeys.has(selectedBlockerKey)
  );
  // Keep recommended actions visible after solved / not-interested.
  const showSelectedBlockerActions = selectedLinks.length > 0;

  const impactLabel = (impact) => {
    if (impact === 'high') return messages.impactHigh;
    if (impact === 'low') return messages.impactLow;
    return messages.impactMedium;
  };

  // Epistemic type distribution for Do-next stale detection.
  const canvasEpistemicNonce = useBoardStore(s => s.canvasEpistemicNonce || 0);
  const epistemicHash = useMemo(() => {
    const counts = { assumption: 0, hypothesis: 0, fact: 0 };
    for (const a of activities || []) {
      const t = String(a?.epistemicType || '').toLowerCase();
      if (Object.prototype.hasOwnProperty.call(counts, t)) counts[t] += 1;
    }
    return `${counts.assumption},${counts.hypothesis},${counts.fact}|${canvasEpistemicNonce}`;
  }, [activities, canvasEpistemicNonce]);

  const lastRefreshedEpistemicHashRef = useRef(null);
  const prevContextLoadingRef = useRef(contextLoading);
  useEffect(() => {
    const wasLoading = prevContextLoadingRef.current;
    prevContextLoadingRef.current = contextLoading;
    if (wasLoading && !contextLoading) {
      lastRefreshedEpistemicHashRef.current = epistemicHash;
    }
  }, [contextLoading, epistemicHash]);

  const epistemicTypeChanged = useMemo(() => {
    const last = lastRefreshedEpistemicHashRef.current;
    return last !== null && last !== epistemicHash;
  // lastRefreshedEpistemicHashRef is a ref — track epistemicHash and
  // contextLoading to re-evaluate after each load cycle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epistemicHash, contextLoading]);

  const softProgressCount = useMemo(() => {
    const current = doneBlockerKeys.size + notInterestedBlockerKeys.size + partialBlockerKeys.size;
    return Math.max(0, current - baseProgressCountRef.current);
  // baseProgressCountRef is a ref — track the state Sets to re-evaluate on each mark.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneBlockerKeys, notInterestedBlockerKeys, partialBlockerKeys]);

  const doNextNeedsRefresh = useMemo(() => {
    if (contextLoading) return false;
    if (boardNeedsStepsConstraintsPatch(board)) return true;
    const blockersOpenCount = blockers.filter((b, i) => {
      const key = blockerDoneKey(b, i);
      return !doneBlockerKeys.has(key) && !notInterestedBlockerKeys.has(key);
    }).length;
    const validationDoneCount = validationQueue.reduce((n, item) => (
      doneValidationKeys.has(String(item?.key || '').trim()) ? n + 1 : n
    ), 0);
    return doNextRefreshWouldMatter({
      triedActionCount: triedActionKeys.size,
      softProgressCount,
      blockersOpen: blockersOpenCount,
      blockersTotal: blockers.length,
      validationsOpen: Math.max(0, validationQueue.length - validationDoneCount),
      validationsTotal: validationQueue.length,
      epistemicTypeChanged,
    });
  }, [
    contextLoading,
    board,
    blockers,
    doneBlockerKeys,
    notInterestedBlockerKeys,
    triedActionKeys,
    softProgressCount,
    validationQueue,
    doneValidationKeys,
    epistemicTypeChanged,
  ]);

  const refreshDoNext = useCallback(() => {
    if (!doNextNeedsRefresh || contextLoading) return;
    if (!doNextExpanded) {
      setDoNextExpanded(true);
      try {
        window.localStorage?.setItem(DO_NEXT_EXPANDED_STORAGE_KEY, '1');
      } catch {
        // ignore storage errors
      }
    }
    refreshContextActions?.();
  }, [contextLoading, doNextExpanded, doNextNeedsRefresh, refreshContextActions]);

  const validationSection = (workingConclusion || validationQueue.length) ? (
    <Box className="board-ai-validation-block">
      {workingConclusion ? (
        <Typography variant="caption" className="board-ai-working-conclusion" color="text.secondary">
          <span className="board-ai-working-conclusion-label">{messages.workingConclusion}</span>
          {workingConclusion}
        </Typography>
      ) : null}
      {validationQueue.length ? (
        <Box className="board-ai-blockers-row board-ai-validation-row">
          <Typography variant="caption" className="board-ai-blockers-label" color="text.secondary">
            {messages.validateNext}
          </Typography>
          <Box className="board-ai-blocker-chips">
            {validationQueue.map((item) => {
              const key = String(item.key || '').trim();
              const isDone = doneValidationKeys.has(key);
              const prefix = item.probeKind === 'ask'
                ? messages.askPrefix
                : messages.searchPrefix;
              const tooltip = [
                item.whyItMatters,
                impactLabel(item.impactIfFails),
                isDone ? messages.missingDone : messages.willRunKind(prefix),
              ].filter(Boolean).join(' · ');
              const chip = (
                <Chip
                  label={(
                    <span className="board-ai-blocker-chip-label">
                      {isDone ? (
                        <CheckIcon className="board-ai-blocker-done-icon" fontSize="small" />
                      ) : (
                        <span className="board-ai-link-mark">
                          {item.impactIfFails === 'high' ? '!' : '·'}
                        </span>
                      )}
                      <span>{item.claim}</span>
                    </span>
                  )}
                  size="small"
                  variant="outlined"
                  clickable={!contextLoading}
                  onClick={() => runValidationProbe(item)}
                  onDelete={isDone ? undefined : () => markValidationDone(key)}
                  deleteIcon={(
                    <CheckIcon fontSize="small" titleAccess={messages.markValidationDone} />
                  )}
                  className={[
                    'board-ai-blocker-chip',
                    'board-ai-validation-chip',
                    isDone ? 'is-blocker-done' : '',
                    `is-impact-${item.impactIfFails || 'medium'}`,
                  ].filter(Boolean).join(' ')}
                />
              );
              return tooltip ? (
                <Tooltip key={key || item.claim} title={tooltip} {...RECOMMENDATION_TOOLTIP_PROPS}>
                  <span className="board-ai-blocker-chip-wrap">{chip}</span>
                </Tooltip>
              ) : (
                <span key={key || item.claim} className="board-ai-blocker-chip-wrap">{chip}</span>
              );
            })}
          </Box>
        </Box>
      ) : null}
    </Box>
  ) : null;

  const blockersSection = blockers.length ? (
    <Box className="board-ai-blockers-row">
      <Typography variant="caption" className="board-ai-blockers-label" color="text.secondary">
        {messages.stillMissing}
      </Typography>
      <Box className="board-ai-blocker-chips">
        {blockers.map((blocker, index) => {
          const links = linksByBlocker.get(index) || [];
          const hasLinks = links.length > 0;
          const key = blockerDoneKey(blocker, index);
          const isDone = doneBlockerKeys.has(key);
          const isPartial = !isDone && partialBlockerKeys.has(key);
          const isNotInterested = notInterestedBlockerKeys.has(key);
          const partialPct = isPartial
            ? (partialPercentForBlocker(
              contextActions?.userProgress?.partialFills,
              key,
              blocker.title
            ) ?? 50)
            : null;
          const detail = [blocker.why, blocker.howToFill].filter(Boolean).join(' · ');
          const linkedHint = hasLinks
            ? messages.showsRecommendedSteps(links.length)
            : '';
          const partialHint = isPartial
            ? (typeof messages.partiallyFilledPct === 'function'
              ? messages.partiallyFilledPct(partialPct)
              : `${messages.partiallyFilled || 'Partially filled'} · ${partialPct}%`)
            : '';
          const doneHint = isNotInterested
            ? messages.notInterested
            : (isDone ? messages.missingDone : partialHint);
          const tooltip = [detail, linkedHint, doneHint].filter(Boolean).join(' · ');
          const selected = selectedBlockerIndex === index;
          const chip = (
            <Chip
              label={(
                <span className="board-ai-blocker-chip-label">
                  {isDone ? (
                    <CheckIcon className="board-ai-blocker-done-icon" fontSize="small" />
                  ) : isPartial ? (
                    <span className="board-ai-link-mark board-ai-partial-mark">{partialPct}%</span>
                  ) : (
                    <span className="board-ai-link-mark">{index + 1}</span>
                  )}
                  <span>{blocker.title}</span>
                </span>
              )}
              style={isPartial ? { '--partial-pct': `${partialPct}%` } : undefined}
              size="small"
              variant="outlined"
              clickable
              onClick={() => {
                setSelectedBlockerIndex(index);
                setActiveActionKey(links[0]?.actionKey || null);
                // Select done and partially-filled shapes for this blocker
                const fillShapeIds = collectFillShapeIds(
                  key,
                  contextActions?.userProgress?.doneFills,
                  contextActions?.userProgress?.partialFills
                );
                if (fillShapeIds.length) {
                  const editor = editorRef?.current;
                  if (editor) {
                    editor.setSelectedShapes(fillShapeIds);
                    try {
                      editor.zoomToSelection?.({ animation: { duration: 200 } });
                    } catch {
                      // ignore camera helpers missing in tests
                    }
                  }
                }
              }}
              onMouseEnter={() => {
                const firstKey = links[0]?.actionKey || null;
                if (firstKey) setActiveActionKey(firstKey);
              }}
              onMouseLeave={() => {
                if (selectedBlockerIndex == null
                  || selectedBlockerIndex < 0
                  || selectedBlockerIndex >= blockers.length) {
                  setActiveActionKey(null);
                  return;
                }
                const selectedLinks = linksByBlocker.get(selectedBlockerIndex) || [];
                setActiveActionKey(selectedLinks[0]?.actionKey || null);
              }}
              className={[
                'board-ai-blocker-chip',
                selected ? 'is-blocker-linked-active' : '',
                hasLinks ? 'is-linked' : '',
                isDone ? 'is-blocker-done' : '',
                isPartial ? 'is-blocker-partial' : '',
                isNotInterested ? 'is-blocker-not-interested' : '',
              ].filter(Boolean).join(' ')}
            />
          );
          return tooltip ? (
            <Tooltip key={`${blocker.title}-${index}`} title={tooltip} {...RECOMMENDATION_TOOLTIP_PROPS}>
              <span className="board-ai-blocker-chip-wrap">{chip}</span>
            </Tooltip>
          ) : (
            <span key={`${blocker.title}-${index}`} className="board-ai-blocker-chip-wrap">{chip}</span>
          );
        })}
      </Box>
    </Box>
  ) : null;

  const selectedStepButtons = showSelectedBlockerActions
    ? selectedLinks.map((link, index) => renderLinkedStep(link, index))
    : null;

  const fallbackStepButtons = !blockers.length ? (
    <>
      {hasFlexibleNext
        ? nextActions.map((action, index) => renderNextActionButton(action, index))
        : (
          <>
            {searchSuggestions.map(item => renderSearchButton(item))}
            {geminiQuestion ? renderAskButton() : null}
          </>
        )}
    </>
  ) : null;

  const emptySelectedHint = blockers.length
    && selectedBlocker
    && !selectedLinks.length
    ? (
      <Typography variant="caption" color="text.secondary" className="board-ai-selected-blocker-hint">
        {[selectedBlocker.howToFill, selectedBlocker.why].filter(Boolean).join(' · ')
          || messages.noRecommendedStep}
      </Typography>
    )
    : null;

  const coverageStatusLabel = (status) => {
    if (status === 'missing') return messages.coverageMissing;
    if (status === 'thin') return messages.coverageThin;
    if (status === 'covered') return messages.coverageCovered;
    return '';
  };

  const selectedBlockerGrounding = selectedBlocker
    && (selectedBlocker.coverageStatus
      || selectedBlocker.clusterLabels?.length
      || selectedBlocker.exemplarPinIds?.length)
    ? (
      <Box className="board-ai-blocker-grounding">
        {selectedBlocker.coverageStatus ? (
          <Typography variant="caption" color="text.secondary" component="span">
            {coverageStatusLabel(selectedBlocker.coverageStatus)}
          </Typography>
        ) : null}
        {selectedBlocker.clusterLabels?.length ? (
          <Typography variant="caption" color="text.secondary" component="span">
            {messages.groundedByClusters(selectedBlocker.clusterLabels.slice(0, 2).join(', '))}
          </Typography>
        ) : null}
        {selectedBlocker.exemplarPinIds?.length || selectedBlocker.clusterIds?.length ? (
          <Button
            size="small"
            variant="text"
            className="board-ai-highlight-on-canvas"
            onClick={() => highlightBlockerOnCanvas(selectedBlocker)}
          >
            {messages.highlightOnCanvas}
          </Button>
        ) : null}
      </Box>
    )
    : null;

  const solvedButton = selectedBlocker ? (
    <Tooltip
      title={selectedBlockerDone ? messages.unsolvedTooltip : messages.solvedTooltip}
      {...RECOMMENDATION_TOOLTIP_PROPS}
    >
      <span className="board-ai-context-action-btn-tooltip-wrap">
        <Button
          size="small"
          variant={selectedBlockerDone ? 'contained' : 'outlined'}
          color="inherit"
          disabled={contextLoading}
          startIcon={<CheckIcon fontSize="small" />}
          onClick={toggleSelectedBlockerSolved}
          className={[
            'board-ai-context-action-btn',
            'board-ai-solved-btn',
            selectedBlockerDone ? 'is-solved' : '',
          ].filter(Boolean).join(' ')}
          aria-pressed={selectedBlockerDone}
        >
          <span className="board-ai-context-action-label">
            {messages.solved}
          </span>
        </Button>
      </span>
    </Tooltip>
  ) : null;

  const notInterestedButton = selectedBlocker ? (
    <Tooltip
      title={selectedBlockerNotInterested
        ? messages.interestedAgainTooltip
        : messages.notInterestedTooltip}
      {...RECOMMENDATION_TOOLTIP_PROPS}
    >
      <span className="board-ai-context-action-btn-tooltip-wrap">
        <Button
          size="small"
          variant={selectedBlockerNotInterested ? 'contained' : 'outlined'}
          color="inherit"
          disabled={contextLoading}
          startIcon={selectedBlockerNotInterested
            ? <ThumbUpOffAltIcon fontSize="small" />
            : <ThumbDownOffAltIcon fontSize="small" />}
          onClick={toggleSelectedBlockerNotInterested}
          className={[
            'board-ai-context-action-btn',
            'board-ai-not-interested-btn',
            selectedBlockerNotInterested ? 'is-not-interested' : '',
          ].filter(Boolean).join(' ')}
          aria-pressed={selectedBlockerNotInterested}
        >
          <span className="board-ai-context-action-label">
            {selectedBlockerNotInterested
              ? messages.interestedAgain
              : messages.notInterested}
          </span>
        </Button>
      </span>
    </Tooltip>
  ) : null;

  const gapMetaActions = (solvedButton || notInterestedButton) ? (
    <Box className="board-ai-gap-meta-actions">
      {solvedButton}
      {notInterestedButton}
    </Box>
  ) : null;

  return (
    <Box className="board-canvas-toolbar board-ai-toolbar">
      <Box
        className={[
          'board-ai-toolbar-row',
          'board-ai-suggested-row',
          doNextExpanded ? 'is-expanded' : 'is-collapsed',
        ].join(' ')}
      >
        <Box className="board-ai-suggested-header">
          <button
            type="button"
            className="board-ai-suggested-toggle"
            onClick={toggleDoNextExpanded}
            aria-expanded={doNextExpanded}
            aria-label={doNextExpanded ? messages.collapseDoNext : messages.expandDoNext}
          >
            <ExpandMoreIcon
              className={[
                'board-ai-suggested-chevron',
                doNextExpanded ? 'is-expanded' : '',
              ].filter(Boolean).join(' ')}
              fontSize="small"
            />
            <Typography variant="caption" className="board-ai-suggested-title" component="span">
              {messages.doNext}
            </Typography>
            {contextLoading && doNextRefreshBrief ? (
              <Typography
                variant="caption"
                color="text.secondary"
                className="board-ai-suggested-header-brief is-refreshing"
                component="span"
                role="status"
                aria-live="polite"
              >
                {doNextRefreshBrief}
              </Typography>
            ) : !doNextExpanded && readCurrentStep(board) ? (
              <Typography
                variant="caption"
                color="text.secondary"
                className="board-ai-suggested-collapsed-goal"
                component="span"
              >
                {readCurrentStep(board)}
              </Typography>
            ) : null}
          </button>
          <HowToPinButton
            className="board-ai-do-next-help"
            stale={doNextNeedsRefresh}
            loading={contextLoading}
            onRefresh={refreshDoNext}
          />
        </Box>

        <Collapse in={doNextExpanded} timeout="auto" unmountOnExit={false}>
          <Box className="board-ai-suggested-body">
            <Box
              className={[
                'board-ai-current-step-row',
                goalCoachRefresh ? 'is-coach-refreshing' : '',
              ].filter(Boolean).join(' ')}
            >
              <BoardCurrentStep
                title={readCurrentStep(board)}
                steps={board?.steps || []}
                currentStepId={board?.currentStepId || ''}
                achievementRate={displayedAchievementRate}
              />
              {progressSummary && !goalCoachRefresh ? (
                <Box className="board-ai-step-explain" aria-label={messages.currentStepExplainLabel}>
                  <Typography
                    variant="caption"
                    className="board-ai-step-explain-label"
                    color="text.secondary"
                  >
                    {messages.currentStepExplainLabel}
                  </Typography>
                  <Typography variant="caption" className="board-ai-progress-line" color="text.secondary">
                    {progressSummary}
                  </Typography>
                </Box>
              ) : null}
            </Box>

            {/* Hide stale "still missing" chips while a goal change is refreshing —
                otherwise they'd linger next to the loading indicator below. */}
            {showThinkingInSlot ? null : blockersSection}

            {/* Thinking only when empty; otherwise keep prior actions so height stays stable. */}
            <Box
              className={[
                'board-ai-next-step-slot',
                showThinkingInSlot ? 'is-thinking' : '',
              ].filter(Boolean).join(' ')}
            >
              {showThinkingInSlot ? (
                <BoardAiThinking
                  active
                  variant="context"
                  className="board-ai-suggested-thinking"
                  language={geminiLanguage}
                  thinkingPhase={thinkingPhase}
                />
              ) : showRecommendationActions ? (
                <Box className="board-ai-toolbar-actions">
                  <Box className="board-ai-toolbar-action-buttons-row">
                    {selectedStepButtons || fallbackStepButtons}
                    {gapMetaActions}
                  </Box>
                  {selectedBlockerGrounding}
                  {emptySelectedHint}
                </Box>
              ) : null}
            </Box>
            <BoardAiJobDebug metrics={lastJobMetrics} className="board-ai-context-job-debug" />
            <BoardAiInputDebug input={lastJobMetrics?.aiInput} className="board-ai-context-input-debug" />

            {validationSection}
          </Box>
        </Collapse>
      </Box>

      {planAction ? (
        <BoardPlanModal
          open={Boolean(planAction)}
          planAction={planAction}
          planCustomAnswer={planCustomAnswer}
          onCustomAnswerChange={setPlanCustomAnswer}
          onApply={({ option, answer, destination }) => void handleDecideAnswer(
            planAction,
            answer,
            option,
            { destination },
          )}
          onClose={closePlan}
          loading={loading}
          resolvePlanActionKind={resolvePlanActionKind}
          formatPlanOptionLabel={option => formatPlanOptionLabel(option, geminiLanguage)}
          kindMeta={kindMeta}
          messages={messages}
        />
      ) : null}

      <BoardAiThinking
        active={loading}
        variant={loadingVariant}
        className="board-ai-action-thinking"
        language={geminiLanguage}
      />

      {contextError ? (
        <Box className="board-ai-context-status-row">
          <Typography variant="caption" color="error">
            {contextError}
          </Typography>
          <Button size="small" onClick={refreshContextActions}>
            Retry
          </Button>
        </Box>
      ) : null}

      {error ? (
        <Typography variant="caption" color="error" className="board-ai-message">
          {error}
        </Typography>
      ) : null}

      {status ? (
        <Box className="board-ai-preview board-ai-applied-status">
          <Typography variant="caption" color="text.secondary" display="block">
            {status.summary}
            {status.canUndo ? ' · Selected changes on the board' : ''}
          </Typography>
          <Box className="board-ai-preview-actions">
            {status.canUndo ? (
              <Button size="small" variant="outlined" onClick={handleUndoAiEdit}>
                Undo
              </Button>
            ) : null}
            <Button size="small" onClick={handleDismissStatus}>
              Dismiss
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default BoardAiToolbar;
